// Shared time/duration constants. Used by the API runtime (auth, jobs, VBA
// caches) and the frontends (React Query staleTime, cookies, relative-time
// helpers). Dependency-free; safe to import from any package.

/** Base unit conversions. */
export const MS_PER_SECOND = 1000;
export const SECONDS_PER_MINUTE = 60;
export const MINUTES_PER_HOUR = 60;
export const HOURS_PER_DAY = 24;
export const DAYS_PER_WEEK = 7;

/** Derived millisecond spans. */
export const MS_PER_MINUTE = MS_PER_SECOND * SECONDS_PER_MINUTE;
export const MS_PER_HOUR = MS_PER_MINUTE * MINUTES_PER_HOUR;
export const MS_PER_DAY = MS_PER_HOUR * HOURS_PER_DAY;

/** Derived second spans. */
export const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * MINUTES_PER_HOUR;
export const SECONDS_PER_DAY = SECONDS_PER_HOUR * HOURS_PER_DAY;
export const SECONDS_PER_WEEK = SECONDS_PER_DAY * DAYS_PER_WEEK;

/**
 * React Query `staleTime` presets (milliseconds). Pick the closest preset when
 * tuning cache freshness rather than hand-rolling `n * 60 * 1000`.
 */
export const STALE_TIME_10_SEC = 10 * MS_PER_SECOND;
export const STALE_TIME_30_SEC = 30 * MS_PER_SECOND;
export const STALE_TIME_1_MIN = MS_PER_MINUTE;
export const STALE_TIME_5_MIN = 5 * MS_PER_MINUTE;
export const STALE_TIME_15_MIN = 15 * MS_PER_MINUTE;
/** Presigned avatar/resource URLs live for 1h; refresh just before expiry. */
export const STALE_TIME_50_MIN = 50 * MS_PER_MINUTE;

/** Better Auth session/cookie durations (seconds). */
export const SESSION_EXPIRY_SECONDS = SECONDS_PER_WEEK;
export const SESSION_UPDATE_AGE_SECONDS = SECONDS_PER_DAY;
export const SESSION_COOKIE_CACHE_SECONDS = 5 * SECONDS_PER_MINUTE;

/** Sidebar open/closed state cookie lifetime (seconds). */
export const SIDEBAR_COOKIE_MAX_AGE_SECONDS = SECONDS_PER_WEEK;

/** BullMQ job retention (seconds): keep completed 24h, failed 7d. */
export const JOB_RETENTION_COMPLETED_SECONDS = SECONDS_PER_DAY;
export const JOB_RETENTION_FAILED_SECONDS = SECONDS_PER_WEEK;

/**
 * Whole days between two instants (`from` before `to`), floored. Replaces the
 * copy-pasted `Math.floor(diff / (1000 * 60 * 60 * 24))` idiom. Accepts `Date`
 * or epoch-millisecond numbers.
 */
export function daysBetween(from: Date | number, to: Date | number): number {
  const fromMs = from instanceof Date ? from.getTime() : from;
  const toMs = to instanceof Date ? to.getTime() : to;
  return Math.floor((toMs - fromMs) / MS_PER_DAY);
}
